import { useState } from "react";
import { Button, Flex, Loader, ModalV2, Select, Text } from "@jsq/ds/core";
import { Colors } from "@jsq/ds/styles";
import {
  useGetGpBillingReviewersQuery,
  useGetGpCsmManagerReviewersQuery,
} from "~/graphql/supergraphHooks";
import { SPACING } from "~/styles/variables";
import type { EumApproverPickerModalProps } from "./types";

export const EumApproverPickerModal = ({
  isOpen,
  onClose,
  onSubmit,
}: EumApproverPickerModalProps) => {
  const [billingApprover, setBillingApprover] = useState<string>("");
  const [csmManagerApprover, setCsmManagerApprover] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    data: billingData,
    loading: billingLoading,
    error: billingError,
  } = useGetGpBillingReviewersQuery({
    skip: !isOpen,
    fetchPolicy: "cache-and-network",
  });

  const {
    data: csmData,
    loading: csmLoading,
    error: csmError,
  } = useGetGpCsmManagerReviewersQuery({
    skip: !isOpen,
    fetchPolicy: "cache-and-network",
  });

  const billingReviewers = billingData?.getGpBillingReviewers ?? [];
  const csmReviewers = csmData?.getGpCsmManagerReviewers ?? [];

  const loading = billingLoading || csmLoading;
  const error = billingError || csmError;

  const handleBillingChange = (value: string | number | string[] | number[]) => {
    if (typeof value === "string") {
      setBillingApprover(value);
    }
  };

  const handleCsmManagerChange = (value: string | number | string[] | number[]) => {
    if (typeof value === "string") {
      setCsmManagerApprover(value);
    }
  };

  const handleClose = () => {
    setBillingApprover("");
    setCsmManagerApprover("");
    setIsSubmitting(false);
    onClose();
  };

  const handleSubmit = async () => {
    const billingParsed = Number(billingApprover);
    const csmParsed = Number(csmManagerApprover);
    if (
      Number.isNaN(billingParsed) ||
      billingParsed <= 0 ||
      Number.isNaN(csmParsed) ||
      csmParsed <= 0
    ) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit({ billingApproverId: billingParsed, csmManagerApproverId: csmParsed });
      handleClose();
    } catch {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  const isSubmitDisabled = !billingApprover || !csmManagerApprover || loading || isSubmitting;

  return (
    <ModalV2 show={isOpen} onHide={handleClose} title="Assign approvers">
      <ModalV2.Body>
        <Flex flexDirection="column" gap={SPACING.MEDIUM}>
          {loading && <Loader loading />}
          {error && (
            <Text variant="regular-md" color={Colors.Danger600}>
              Failed to load approvers
            </Text>
          )}
          {!loading && !error && (
            <>
              <Select
                label="Billing approver"
                placeholder="Select billing approver"
                value={billingApprover}
                onChange={handleBillingChange}
                full
                data-testid="billing-approver-select"
              >
                <Select.Section>
                  {billingReviewers.map((reviewer) => (
                    <Select.Option key={reviewer.id} value={String(reviewer.id)}>
                      {reviewer.displayName}
                    </Select.Option>
                  ))}
                </Select.Section>
              </Select>
              <Select
                label="CSM manager approver"
                placeholder="Select CSM manager"
                value={csmManagerApprover}
                onChange={handleCsmManagerChange}
                full
                data-testid="csm-manager-approver-select"
              >
                <Select.Section>
                  {csmReviewers.map((reviewer) => (
                    <Select.Option key={reviewer.id} value={String(reviewer.id)}>
                      {reviewer.displayName}
                    </Select.Option>
                  ))}
                </Select.Section>
              </Select>
            </>
          )}
        </Flex>
      </ModalV2.Body>
      <ModalV2.Footer>
        <Flex justifyContent="flex-end" gap={SPACING.SMALL}>
          <Button variant="neutral" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => void handleSubmit()}
            disabled={isSubmitDisabled}
          >
            Submit for approval
          </Button>
        </Flex>
      </ModalV2.Footer>
    </ModalV2>
  );
};
